import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { EmailTemplateService } from '../services/emailTemplateService';
import type { EmailTemplateOrBase } from '../types/EmailTemplateTypes';

interface EmailTemplatePreviewModalProps {
  template: EmailTemplateOrBase;
  onClose: () => void;
}

export const EmailTemplatePreviewModal = ({ template, onClose }: EmailTemplatePreviewModalProps) => {
  const { t } = useTranslation();
  const [rendered, setRendered] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const exampleVariables = template.variables.reduce<Record<string, string>>(
    (acc, v) => ({ ...acc, [v.key]: v.example }),
    {}
  );

  useEffect(() => {
    EmailTemplateService.previewTemplate(template.content, exampleVariables)
      .then((result) => setRendered(result.rendered))
      .catch(() => setRendered(template.content))
      .finally(() => setIsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {t('settings.emailTemplates.preview')} — {template.name} ({template.language})
          </DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <iframe
            srcDoc={rendered}
            title="email-preview"
            style={{ width: '100%', height: '600px', border: 'none' }}
            sandbox="allow-same-origin"
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
